import { httpsCallable, HttpsCallable } from 'firebase/functions';
import { functions } from '@config/firebase';
import * as Sentry from '@sentry/react';

// Firebase Functions instance is initialized in firebase.ts with emulator support

// Set universal timeout for all functions (20 minutes)
const DEFAULT_TIMEOUT = 1200000; // 20 minutes in milliseconds

// Cloud function references (all use DEFAULT_TIMEOUT unless overridden)
const cloudFunctions: Record<string, HttpsCallable<any, any>> = {
  sendEmailSignInLink: httpsCallable(functions, 'sendEmailSignInLink', { timeout: DEFAULT_TIMEOUT }),
  webSearchProfile: httpsCallable(functions, 'webSearchProfile', { timeout: DEFAULT_TIMEOUT }),
  processCSVParsedDataToProfile: httpsCallable(functions, 'processCSVParsedDataToProfile', { timeout: DEFAULT_TIMEOUT }),
  sendIntroductionEmail: httpsCallable(functions, 'sendIntroductionEmail', { timeout: DEFAULT_TIMEOUT }),
  createMentorshipMatches: httpsCallable(functions, 'createMentorshipMatches', { timeout: DEFAULT_TIMEOUT }),
  batchCreateUsers: httpsCallable(functions, 'batchCreateUsers', { timeout: DEFAULT_TIMEOUT }),
  promoteUserToMentor: httpsCallable(functions, 'promoteUserToMentor', { timeout: DEFAULT_TIMEOUT }),
  demoteUserFromMentor: httpsCallable(functions, 'demoteUserFromMentor', { timeout: DEFAULT_TIMEOUT }),
  updateUserPreferences: httpsCallable(functions, 'updateUserPreferences', { timeout: DEFAULT_TIMEOUT }),
  cohortStart: httpsCallable(functions, 'cohortStart', { timeout: DEFAULT_TIMEOUT }),
  cohortTerminate: httpsCallable(functions, 'cohortTerminate', { timeout: DEFAULT_TIMEOUT }),
  // Generic email functions (new)
  generateEmails: httpsCallable(functions, 'generateEmails', { timeout: DEFAULT_TIMEOUT }),
  sendEmails: httpsCallable(functions, 'sendEmails', { timeout: DEFAULT_TIMEOUT }),
  cancelEmails: httpsCallable(functions, 'cancelEmails', { timeout: DEFAULT_TIMEOUT }),
  // Data & Statistics
  getCohortMatches: httpsCallable(functions, 'getCohortMatches', { timeout: DEFAULT_TIMEOUT }),
  // AI Data Query Chatbot
  dataQueryChat: httpsCallable(functions, 'dataQueryChat', { timeout: DEFAULT_TIMEOUT }),
  // Internal admin functions
  listAllTenanciesWithActivity: httpsCallable(functions, 'listAllTenanciesWithActivity', { timeout: DEFAULT_TIMEOUT }),
};

// Send email sign-in link
export async function sendSignInLinkToEmail(email: string, customSettings: any = {}) {
  const currentUrl = window.location.origin + window.location.pathname;
  const redirectUrl = currentUrl;
    
  const defaultSettings = {
    url: redirectUrl,
    handleCodeInApp: false,
  };

  try {
    const result = await cloudFunctions.sendEmailSignInLink({
      email: email,
      actionCodeSettings: { ...defaultSettings, ...customSettings },
      source: "web"
    });

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Error sending sign-in link:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

// Web search profile function
export async function searchProfiles(data: { firstName: string; lastName: string; keyPoints?: string[] }) {
  return callCloudFunction('webSearchProfile', data);
}

// Process CSV data to profiles function
export async function processCSVDataToProfiles(csvData: any[]) {
  return callCloudFunction('processCSVParsedDataToProfile', { csvData });
}

// Send introduction email function
export async function sendIntroductionEmail(data: {
  introducingUid: string;
  introducedToUid: string;
  tenantId?: string;
  introducingMenteeToMentor?: boolean;
}) {
  return callCloudFunction('sendIntroductionEmail', data);
}

// Create mentorship matches function
export async function createMentorshipMatches(data: {
  tenant_id: string;
  cohort_id: string;
}) {
  return callCloudFunction('createMentorshipMatches', data);
}

// Batch create users function
export async function batchCreateUsers(profiles: any[], tenantId?: string) {
  return callCloudFunction('batchCreateUsers', {
    profiles,
    tenant_id: tenantId || null
  });
}

// Promote user to mentor function
export async function promoteUserToMentor(uid?: string) {
  return callCloudFunction('promoteUserToMentor', uid ? { uid } : {});
}

// Demote user from mentor function
export async function demoteUserFromMentor(uid?: string) {
  return callCloudFunction('demoteUserFromMentor', uid ? { uid } : {});
}

// Update a user's custom key/value preferences (fed into matching as a soft signal).
// tenantId is required by the backend when editing a user other than yourself
// (used for the tenant-admin + tenant-membership authorization checks).
export async function updateUserPreferences(
  uid: string,
  preferences: Record<string, string>,
  tenantId?: string | null
) {
  return callCloudFunction('updateUserPreferences', { uid, preferences, tenant_id: tenantId || null });
}

// Start a new cohort
export async function cohortStart(data: {
  tenant_id: string;
  title?: string;
  description?: string;
  users?: string[];
  status?: 'draft' | 'active' | 'completed' | 'terminated';
  templateId?: string;
  customLabels?: Record<string, string>;
  customPrepContent?: string;
  customFeedbackMaterial?: string;
  presetActions?: Array<{ key: string; title: string; description: string; daysAfterPrevious: number; enabled: boolean }>;
  workflowType?: string;
}) {
  return callCloudFunction('cohortStart', data);
}

// Terminate an existing cohort
export async function cohortTerminate(data: {
  tenant_id: string;
  cohort_id: string;
}) {
  return callCloudFunction('cohortTerminate', data);
}

// ============================================================================
// GENERIC EMAIL FUNCTIONS
// ============================================================================

export type EmailType = 'connection' | 'feedback' | 'welcomePackage' | 'introConsent' | 'checkIn';

// Generate emails for a cohort (or preview a sample email)
export async function generateEmails(data: {
  tenant_id: string;
  cohort_id: string;
  email_type: EmailType;
  preview?: boolean;
  preview_role?: 'mentee' | 'mentor';
  example_email?: { subject: string; content: string };
}) {
  return callCloudFunction('generateEmails', data);
}

// Send emails for a cohort
export async function sendEmails(data: {
  tenant_id: string;
  cohort_id: string;
  email_type: EmailType;
  single_id?: string;
}) {
  return callCloudFunction('sendEmails', data);
}

// Get matches for a cohort (with profiles and consent status)
export async function getCohortMatches(data: {
  tenant_id: string;
  cohort_id: string;
}) {
  return callCloudFunction('getCohortMatches', data);
}

// Cancel scheduled emails for a cohort
export async function cancelEmails(data: {
  tenant_id: string;
  cohort_id: string;
  email_type: EmailType;
}) {
  return callCloudFunction('cancelEmails', data);
}

// Generic function caller for other cloud functions
export async function callCloudFunction(functionName: string, data: any) {
  if (!cloudFunctions[functionName]) {
    console.error(`❌ Cloud function '${functionName}' not found`);
    return { success: false, error: `Function ${functionName} not found` };
  }

  try {
    console.log(`Calling cloud function: ${functionName}`);
    if (process.env.NODE_ENV === 'development') {
      console.log('Payload:', data);
    } else {
      const keys = data && typeof data === 'object' ? Object.keys(data) : [];
      console.log('Payload keys:', keys);
    }

    const result = await cloudFunctions[functionName](data);

    console.log(`${functionName} success`);
    if (process.env.NODE_ENV === 'development') {
      console.log('Response:', result.data);
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error(`❌ Error calling ${functionName}:`, error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      details: error.details
    });
    Sentry.captureException(error, {
      tags: { cloudFunction: functionName },
      extra: { payload: data, code: error.code, details: error.details },
    });
    return {
      success: false,
      error: error.message || 'Unknown error occurred',
      details: error.details || error.code || 'No additional details'
    };
  }
}

// ============================================================================
// AI DATA QUERY CHATBOT
// ============================================================================

export interface DataQueryResponse {
  type: 'text' | 'table' | 'summary' | 'list' | 'csv';
  title: string;
  data: {
    message?: string;
    headers?: string[];
    rows?: string[][];
    metrics?: Array<{ label: string; value: string; change: string; trend: 'up' | 'down' | 'neutral' }>;
    items?: string[];
  };
}

export async function dataQueryChat(data: {
  tenant_id: string;
  query: string;
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}): Promise<{ success: boolean; data?: { response: DataQueryResponse }; error?: string; details?: any }> {
  return callCloudFunction('dataQueryChat', data);
}

// ============================================================================
// INTERNAL ADMIN FUNCTIONS
// ============================================================================

export interface TenancyUser {
  uid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: 'mentor' | 'mentee';
  createdAt: number | null;
  lastCohortAction: number | null;
}

export interface TenancyAction {
  action: string;
  status: string;
  date: number | null;
  triggeredBy: string | null;
  cohortId: string;
  cohortTitle: string;
}

export interface TenancyMatch {
  id: string;
  mentorId: string | null;
  menteeId: string | null;
  matchQuality: number | null;
  createdAt: number | null;
  hasFeedback: boolean;
}

export interface TenancyCohort {
  id: string;
  title: string;
  status: string;
  userCount: number;
  createdAt: number | null;
  workflowType: string | null;
  matches: TenancyMatch[];
}

export interface TenancyWithActivity {
  id: string;
  title: string;
  adminEmail: string | null;
  disabled: boolean;
  createdAt: number | null;
  userCount: number;
  cohortCount: number;
  actions: TenancyAction[];
  cohorts: TenancyCohort[];
  lastActionDate: number | null;
  users: TenancyUser[];
}

// List all tenancies with activity (internal admin only)
export async function listAllTenanciesWithActivity(): Promise<{
  success: boolean;
  data?: TenancyWithActivity[];
  error?: string;
}> {
  return callCloudFunction('listAllTenanciesWithActivity', {});
}

// Example usage
/*
// For email sign-in
const emailResult = await sendSignInLinkToEmail('user@example.com');

// For generic cloud function
const result = await callCloudFunction('someFunction', { param1: 'value1' });
*/